import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FileUpload} from '@/components/atoms'
import {useAppDispatch} from '@/store'
import {Constants, NUMBERS} from '@/shared/constants/commonConstants'
import {uploadFileCSVError, uploadImageCSVError} from '@/shared/utils/commonTemplate'
import {useFileUpload} from '@/hooks/useFileUpload'
import {noop} from '@/shared/utils/noop'
/**
 * UploadFile component allows users to upload files with various configurations and validations.
 *
 * @param {Object} props - The properties object.
 * @param {string[]} props.acceptedExtensions - List of accepted file extensions.
 * @param {string} props.description - Description for the file upload.
 * @param {string} props.label - Label for the file upload input.
 * @param {File[]} props.files - Array of files to be uploaded.
 * @param {boolean} [props.multiple=false] - Allow multiple file uploads.
 * @param {string} [props.className=''] - Additional class names for the component.
 * @param {string} [props.containerClassName=''] - Additional class names for the container.
 * @param {boolean} [props.required=false] - Whether the file upload is required.
 * @param {string} props.variant - Variant of the file upload component.
 * @param {string} [props.buttonLabel=Constants.UploadFile] - Label for the upload button.
 * @param {string} props.type - Type of the file upload.
 * @param {string} [props.imageUri=''] - URI of the image to be uploaded.
 * @param {Function} [props.setFile=() => {}] - Function to set the file.
 * @param {Function} props.uploadFileSuccess - Function to handle successful file upload.
 * @param {Function} props.resetUploadFile - Function to reset the file upload.
 * @param {boolean} [props.isDark=true] - Whether the component is in dark mode.
 * @param {Function} [props.onUpload=() => {}] - Function to handle file upload.
 * @param {string} props.feedbackType - Type of feedback to be displayed.
 * @param {Function} [props.onUploadDelete=() => {}] - Function to handle file upload deletion.
 * @param {string} [props.helperText=''] - Helper text to be displayed.
 * @param {Function} [props.onUploadError=() => {}] - Function to handle file upload errors.
 * @param {boolean} [props.fileSizeCheck=false] - Whether to check the file size.
 * @param {string} [props.dataTestId=''] - Data test ID for the component.
 * @param {boolean} [props.disabled=false] - Whether the file upload is disabled.
 * @param {Function} [props.onSetError=() => {}] - Function to handle file upload errors and to handle upload modal button.
 *
 * @returns {JSX.Element} The UploadFile component.
 */
const UploadFile = ({
  acceptedExtensions,
  description,
  label,
  files,
  multiple = false,
  className = '',
  containerClassName = '',
  required = false,
  variant,
  buttonLabel = Constants.UploadFile,
  type,
  imageUri = '',
  setFile = noop,
  uploadFileSuccess,
  resetUploadFile,
  isDark = true,
  onUpload = noop,
  feedbackType,
  onUploadDelete = noop,
  helperText = '',
  onUploadError = noop,
  fileSizeCheck = false,
  dataTestId = '',
  disabled = false,
  setSecondaryBtnText,
  uploadFor,
  onSetError = noop,
}) => {
  const {fileVerification, progress} = useFileUpload()

  const [error, setError] = useState(false)
  const [fileError, setFileError] = useState({})
  const [uploading, setUploading] = useState(false)
  const dispatch = useAppDispatch()
  const getBase64 = (file, url) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = function () {
      dispatch(
        uploadFileSuccess({
          title: file.name,
          imageBase64: reader.result,
          imageUri: URL.createObjectURL(file),
          imageUrl: url,
        })
      )
    }
  }

  const getSelectiveFileError = acceptedExtensions => {
    if (acceptedExtensions === Constants.CSV) {
      return uploadFileCSVError()
    } else {
      return uploadImageCSVError()
    }
  }

  //when api is available we can config some changes as per our requirement
  const fileUploadVerification = async (file) => {
    try {
      const response = fileVerification(file, uploadFor);
  
      // Handle the response
      await handleFileUploadResponse(response, file);
  
      // Simulate API response for columns validation
      const data = { columnsValid: false };
      if (response.ok) {
        handleColumnsValidation(response, data, file);
      }
    } catch (err) {
      handleFileUploadError(err);
    }
  };
  
  // Helper function to handle the file upload response
  const handleFileUploadResponse = async (response, file) => {
    try {
      const res = await response;
      if (!res || Object.keys(res).length === NUMBERS.Zero) {
        setFileError({
          feedbackType: Constants.Error,
          helperText: Constants.ErrorForFileUpload,
        });
        setUploading(false);
        onSetError(true);
        return;
      }
  
      if (res.success === false) {
        setFileError({
          feedbackType: Constants.Error,
          helperText: res?.message,
        });
        setUploading(false);
        onSetError(true);
        return;
      }
  
      if (res?.message === Constants.Success) {
        if (uploadFileSuccess) {
          getBase64(file, res?.media?.imageUrl);
        }
        onUpload(file);
        setUploading(false);
        onSetError(false);
      }
    } catch (err) {
      setFileError({ feedbackType: Constants.Error, helperText: err });
      setUploading(false);
      onSetError(true);
    }
  };
  
  // Helper function to handle columns validation
  const handleColumnsValidation = (response, data, file) => {
    if (data.columnsValid) {
      if (uploadFileSuccess) {
        getBase64(file);
      }
      onUpload(file);
      setUploading(false);
      onSetError(false);
    } else {
      setSecondaryBtnText('Download Additional Errors');
      setFileError({
        feedbackType: Constants.Error,
        helperText: getSelectiveFileError(acceptedExtensions),
      });
      setUploading(false);
      onSetError(true);
    }
  };
  
  // Helper function to handle file upload errors
  const handleFileUploadError = (err) => {
    setFileError({
      feedbackType: Constants.Error,
      helperText: `Error verifying columns: ${err.message}`,
    });
    setUploading(false);
    onSetError(true);
  };

  const handleDelete = () => {
    setError(false)
    setFileError({})
    onSetError(false)
    if (resetUploadFile) {
      dispatch(resetUploadFile())
    }
    setFile([])
    onUploadDelete()
  }

  const handleUpload = async file => {
    setError(false)
    setFileError({})
    onSetError(false)
    if (fileSizeCheck) {
      try {
        const fileSize = file.size / NUMBERS.OneThousandTwentyFour / NUMBERS.OneThousandTwentyFour
        if (fileSize > NUMBERS.Fifty) {
          setFileError({feedbackType: 'error', helperText: 'File cannot be uploaded. File size should be below 50MB'})
          return false
        }
      } catch (err) {
        setFileError({feedbackType: 'error', helperText: err + '. Supported formats: .jpg,.png'})
        return false
      }
    }
    setUploading(true)
    setFile([file])
    fileUploadVerification(file)
  }

  const handleUploadError = err => {
    setError(true)
    onUploadError(err)
  }

  const getFeedbackType = () => {
    if (Object.keys(fileError).length > NUMBERS.Zero) {
      return fileError?.feedbackType
    }
    return feedbackType
  }

  const getHelperText = () => {
    if (Object.keys(fileError).length > NUMBERS.Zero) {
      return fileError?.helperText
    }
    return helperText
  }
  const isFileError = Object.keys(fileError).length > 0
  return (
    <FileUpload
      acceptedExtensions={acceptedExtensions}
      description={description}
      label={label}
      variant={uploading ? Constants.Uploading : variant}
      buttonLabel={buttonLabel}
      onUpload={handleUpload}
      onUploadDelete={handleDelete}
      type={type}
      required={required}
      imageUri={imageUri}
      containerClassName={`${!isFileError && containerClassName} ${error || isFileError ? '!border-2 !border-dotted !border-red-500' : ''}`}
      className={className}
      multiple={multiple}
      value={isFileError ? '' : files}
      isDark={isDark}
      feedbackType={getFeedbackType()}
      helperText={getHelperText()}
      onUploadError={handleUploadError}
      dataTestId={dataTestId}
      progressPercentage={progress}
      disabled={disabled}
    />
  )
}

UploadFile.propTypes = {
  acceptedExtensions: PropTypes.arrayOf(PropTypes.string),
  description: PropTypes.string,
  label: PropTypes.string,
  files: PropTypes.array,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  required: PropTypes.bool,
  variant: PropTypes.string,
  buttonLabel: PropTypes.string,
  type: PropTypes.string,
  imageUri: PropTypes.string,
  setFile: PropTypes.func,
  uploadFileSuccess: PropTypes.func,
  resetUploadFile: PropTypes.func,
  isDark: PropTypes.bool,
  onUpload: PropTypes.func,
  feedbackType: PropTypes.string,
  onUploadDelete: PropTypes.func,
  helperText: PropTypes.string,
  onUploadError: PropTypes.func,
  fileSizeCheck: PropTypes.bool,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  setSecondaryBtnText: PropTypes.func,
  uploadFor: PropTypes.string,
  onSetError: PropTypes.func,
}

UploadFile.defaultProps = {
  multiple: false,
  className: '',
  containerClassName: '',
  required: false,
  buttonLabel: Constants.UploadFile,
  imageUri: '',
  setFile: noop,
  isDark: true,
  onUpload: noop,
  onUploadDelete: noop,
  helperText: '',
  onUploadError: noop,
  fileSizeCheck: false,
  dataTestId: '',
  disabled: false,
  onSetError: noop,
}

export default UploadFile
